import { Button } from "@/components/ui/Button";
import CustomSelect from "@/components/ui/CustomSelect";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/Drawer";
import { Label } from "@/components/ui/Label";
import { useSearch } from "@/contexts/SearchContext";
import { PropertyRentalPeriodType } from "@/enums/property.enum";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { RefObject, useRef } from "react";
import { useOnClickOutside, useToggle } from "usehooks-ts";
import { PropertyFilterProps } from "./type";

export const RentalPeriodFilter = ({ variant }: PropertyFilterProps) => {
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("shared");

  const { rentalPeriod, updateRentalPeriod } = useSearch();
  const [open, toggleOpen, setOpen] = useToggle();

  useOnClickOutside(drawerContentRef as RefObject<HTMLElement>, (event) => {
    const target = event.target as HTMLElement;
    const isSelectContent = target.closest('[role="listbox"]');

    if (!isSelectContent) {
      setOpen(false);
    }
  });

  const RentalPeriodButtonGroup = () => {
    return (
      <div className="flex gap-1.5 flex-wrap">
        <Button
          onClick={() => updateRentalPeriod(null)}
          className="capitalize"
          variant={rentalPeriod ? "outline" : "active"}
        >
          {t("filters.common-options.all")}
        </Button>
        {Object.values(PropertyRentalPeriodType).map((value) => (
          <Button
            key={value}
            onClick={() => updateRentalPeriod(value)}
            className="capitalize"
            variant={value == rentalPeriod ? "active" : "outline"}
          >
            {t(`filters.rental-period.options.${value}`)}
          </Button>
        ))}
      </div>
    );
  };

  switch (variant) {
    case "plain":
      return (
        <CustomSelect
          size="sm"
          value={
            rentalPeriod
              ? {
                  value: rentalPeriod,
                  label: t(`filters.rental-period.options.${rentalPeriod}`),
                }
              : null
          }
          variant={rentalPeriod ? "active" : "default"}
          onChange={(option) =>
            option &&
            updateRentalPeriod(option.value! as PropertyRentalPeriodType)
          }
          menuPosition="fixed"
          options={[
            { label: t("filters.common-options.all"), value: null },
            ...Object.values(PropertyRentalPeriodType).map((type) => ({
              value: type,
              label: t(`filters.rental-period.options.${type}`),
            })),
          ]}
          placeholder={t("filters.rental-period.placeholder")}
        />
      );
    case "button":
      return (
        <div className="space-y-2 py-2">
          <Label>{t("words.rental-period")}</Label>
          <RentalPeriodButtonGroup />
        </div>
      );
    case "drawer":
      return (
        <Drawer open={open} key={"amenities-drawer"}>
          <DrawerTrigger asChild className="h-full text-base capitalize">
            <Button
              variant={rentalPeriod ? "active" : "outline"}
              onClick={toggleOpen}
            >
              <span className="max-w-32 truncate">
                {rentalPeriod
                  ? t(`filters.rental-period.options.${rentalPeriod}`)
                  : t("filters.rental-period.placeholder")}
              </span>
              <ChevronDown className="size-4.5" />
            </Button>
          </DrawerTrigger>

          <DrawerContent
            className="flex gap-4 flex-wrap flex-col p-2"
            ref={drawerContentRef}
          >
            <DrawerTitle>{t("filters.rental-period.title")}</DrawerTitle>
            <RentalPeriodButtonGroup />

            <div className="space-x-2 flex items-end">
              <Button
                variant={"secondary"}
                onClick={() => setOpen(false)}
                className="w-full"
              >
                {t("filters.cta.done")}
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
      );
  }
};
